import { formatToDate, formatToTimeShort } from 'helpers';
import { useConvertDateToUserTimezone } from 'hooks';
import { useTranslation } from 'libs/i18n';
import {
  Box,
  CircularProgress,
  Pressed,
  Stack,
  Tooltip,
  Typography,
} from 'libs/ui';
import { Colors } from 'libs/ui/theme';

import { ReadIcon, UnreadIcon } from './icons';
import { VerticalDelimiterStyled } from './styles';

export interface NotificationHeaderProps {
  isRead: boolean;
  date: Date;
  title: string;
  loadingToggleRead?: boolean;
  onToggleRead: () => void;
}

export const NotificationHeader = ({
  isRead,
  title,
  date,
  loadingToggleRead,
  onToggleRead,
}: NotificationHeaderProps) => {
  const { t } = useTranslation();

  const { convertDateToUserTimezone } = useConvertDateToUserTimezone();

  const convertedDate = convertDateToUserTimezone(date);
  const dateFormatted = formatToDate(convertedDate);
  const timeFormatted = formatToTimeShort(convertedDate);

  return (
    <Stack direction="row" justifyContent="space-between">
      <Stack flex={1} spacing={4}>
        <Stack
          alignItems="center"
          direction="row"
          flex={1}
          justifyContent="space-between"
        >
          <Typography variant="bodyBold">{title}</Typography>
          {!isRead && !loadingToggleRead && (
            <Box className="notificationItem-readMark" />
          )}
        </Stack>
        <Stack alignItems="center" direction="row" spacing={4}>
          <Typography color={Colors.gray600} variant="caption">
            {dateFormatted}
          </Typography>
          <VerticalDelimiterStyled />
          <Typography color={Colors.gray600} variant="caption">
            {timeFormatted}
          </Typography>
        </Stack>
      </Stack>
      {loadingToggleRead ? (
        <CircularProgress size={16} />
      ) : (
        <Tooltip
          title={
            !isRead
              ? t('components.dialogNotifications.readMarkTitle')
              : t('components.dialogNotifications.unreadMarkTitle')
          }
        >
          <Pressed className="notificationItem-checkBox" onClick={onToggleRead}>
            {!isRead ? (
              <ReadIcon color={Colors.blue500} />
            ) : (
              <UnreadIcon color={Colors.blue500} />
            )}
          </Pressed>
        </Tooltip>
      )}
    </Stack>
  );
};
